import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, parseDbTimestamp } from "@repo/shared";
import type { TenantUser, TenantUserCreateResponse } from "@repo/shared/types";
import { ArrowRightLeft, KeyRound, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DocButton } from "@/components/ui/doc-button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorCode, del, patch, post } from "@/lib/api/_client";
import { useMe, useUsers } from "@/lib/hooks/use-clients";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { PERMISSIONS } from "@/lib/permissions";
import { getRelativeTime } from "@/lib/utils";
import { ReassignClientsDialog } from "./reassign-clients-dialog";
import { T } from "./users-wrapper.i18n";

const PAGE_SIZE = 20;

export function UsersWrapper() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { me } = useMe();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [reassigning, setReassigning] = useState<TenantUser | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    permissions: ["advisor"] as string[],
  });
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [page, setPage] = useState(1);
  const { confirm, ConfirmationDialog } = useConfirm();

  // QL-136 — search and paging run on the server; this page only reports the
  // admin's choices and shows the page that comes back.
  const { users, ssoUserIds, hasSsoEnabled, pagination, isLoading, isFetching, mutate } = useUsers({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
  });

  // A new search starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // A page can empty out after a delete on the last page; step back to the
  // last page that still exists so the pager stays reachable.
  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        email: editingUser.email,
        name: editingUser.name || "",
        password: "",
        permissions: editingUser.permissions.length > 0 ? editingUser.permissions : ["advisor"],
      });
    } else {
      setFormData({ email: "", name: "", password: "", permissions: ["advisor"] });
    }
    setError(null);
  }, [editingUser, dialogOpen]);

  const handleCreate = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (user: TenantUser) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  // QL-365 — rule violations the API reports with a stable code.
  const errorMessage = (err: unknown): string | undefined => {
    const code = apiErrorCode(err);
    if (code === "last_password_admin") return t.lastPasswordAdmin;
    if (code === "cannot_delete_self") return t.cannotDeleteSelf;
    return undefined;
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData((prev) => {
      const newPermissions = prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission];

      return {
        ...prev,
        permissions: newPermissions.length > 0 ? newPermissions : ["advisor"],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Length check fires only when a password is supplied, so editing without
    // changing the password (blank field) still keeps the current password.
    if (formData.password) {
      if (formData.password.length < PASSWORD_MIN_LENGTH) {
        setError(t.passwordMinLength);
        return;
      }
      if (formData.password.length > PASSWORD_MAX_LENGTH) {
        setError(t.passwordMaxLength);
        return;
      }
    }

    startTransition(async () => {
      try {
        const url = editingUser ? `/internal/users/${editingUser.id}` : "/internal/users";
        if (editingUser) {
          await patch(url, formData);
        } else {
          const created = await post<TenantUserCreateResponse>(url, formData);
          if (created.restored) toast.success(t.userRestored);
        }

        setFormData({ email: "", name: "", password: "", permissions: ["advisor"] });
        setDialogOpen(false);
        setEditingUser(null);
        mutate();
      } catch (err: any) {
        setError(errorMessage(err) || err.message || t.failedToSaveUser);
      }
    });
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    const confirmed = await confirm({
      title: t.deleteUser,
      description: t.deleteUserConfirmation(userEmail),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/users/${userId}`);
        mutate();
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: errorMessage(err) || err.message || t.failedToDeleteUser,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    });
  };

  const handleDisable2FA = async (userId: string, userEmail: string) => {
    const confirmed = await confirm({
      title: t.disableTwoFactor,
      description: t.disableTwoFactorConfirmation(userEmail),
      confirmText: t.disable2FA,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await post(`/internal/users/${userId}/disable-2fa`);
        mutate();
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err.message || t.failedToDisable2FA,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    });
  };

  // Check if the user being edited is an SSO user
  const isEditingSsoUser = editingUser ? ssoUserIds.includes(editingUser.id) : false;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.users }]}
        title={t.users}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="users" variant="outline" size="sm">
            {t.documentation}
          </DocButton>
        }
      />

      <div className="flex items-center justify-between gap-4 mb-4">
        <SearchInput
          className="flex-1 max-w-sm"
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <Button onClick={handleCreate}>{t.addUser}</Button>
      </div>

      {isLoading ? null : users.length === 0 && !debouncedSearch.trim() ? (
        <EmptyState>
          {t.noUsersYet}
          <span className="block text-sm mt-2">{t.addUsersToGetStarted}</span>
        </EmptyState>
      ) : users.length === 0 ? (
        <EmptyState>{t.noSearchResults}</EmptyState>
      ) : (
        <>
          <Table
            variant="data"
            containerClassName={
              isFetching ? "opacity-50 pointer-events-none transition-opacity" : ""
            }
          >
            <TableHeader>
              <TableRow>
                <TableHead>{t.email}</TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.auth}</TableHead>
                <TableHead>{t.twoFa}</TableHead>
                <TableHead>{t.permissions}</TableHead>
                <TableHead>{t.lastActive}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const isSsoUser = ssoUserIds.includes(user.id);
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.name || "-"}</TableCell>
                    <TableCell>
                      {isSsoUser ? (
                        <Badge variant="secondary" className="gap-1">
                          <KeyRound className="h-3 w-3" />
                          {t.sso}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{t.password}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.twoFactorEnabled ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="success">{t.enabled}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => handleDisable2FA(user.id, user.email)}
                            disabled={isPending}
                          >
                            {t.disable}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.lastLogin ? (
                        <TimeAgoTooltip date={parseDbTimestamp(user.lastLogin)}>
                          {getRelativeTime(user.lastLogin, userLanguage)}
                        </TimeAgoTooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setReassigning(user)}
                          disabled={isPending}
                          tooltip={t.moveClients}
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(user)}
                          disabled={isPending}
                          tooltip={t.editUser}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          // QL-365 — the API refuses deleting yourself as well.
                          disabled={isPending || user.id === me?.id}
                          tooltip={user.id === me?.id ? t.cannotDeleteSelf : t.delete}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalCount={pagination.totalCount}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
          />
        </>
      )}

      {hasSsoEnabled && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-info/40 bg-info/10">
          <KeyRound className="h-5 w-5 text-info mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-foreground">{t.ssoEnabled}</p>
            <p className="text-muted-foreground mt-1">{t.ssoEnabledDescription}</p>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={() => handleDialogClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser
                ? isEditingSsoUser
                  ? t.editSsoUserPermissions
                  : t.editUser
                : t.addNewUser}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? isEditingSsoUser
                  ? t.editSsoUserDescription
                  : t.editUserDescription
                : t.addNewUserDescription}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              {/* Show SSO indicator when editing SSO user */}
              {isEditingSsoUser && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{editingUser?.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {editingUser?.name || t.ssoUser} - {t.managedByIdp}
                    </p>
                  </div>
                </div>
              )}

              {/* Only show email/name/password fields for non-SSO users */}
              {!isEditingSsoUser && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t.emailRequired} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t.emailPlaceholder}
                      required
                      disabled={isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">{t.nameLabel}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t.namePlaceholder}
                      disabled={isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">
                      {t.passwordLabel}{" "}
                      {!editingUser && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={
                        editingUser ? t.leaveBlankPasswordPlaceholder : t.passwordPlaceholder
                      }
                      required={!editingUser}
                      disabled={isPending}
                    />
                    <p className="text-xs text-muted-foreground">{t.passwordMinHint}</p>
                    {editingUser && (
                      <p className="text-xs text-muted-foreground">{t.leaveBlankToKeepPassword}</p>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-3">
                <Label>
                  {t.permissionsLabel} <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="perm-admin"
                      checked={formData.permissions.includes(PERMISSIONS.ADMIN)}
                      onCheckedChange={() => handlePermissionToggle(PERMISSIONS.ADMIN)}
                      disabled={isPending}
                    />
                    <Label htmlFor="perm-admin" className="font-normal cursor-pointer">
                      {t.adminPermission}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="perm-advisor"
                      checked={formData.permissions.includes(PERMISSIONS.ADVISOR)}
                      onCheckedChange={() => handlePermissionToggle(PERMISSIONS.ADVISOR)}
                      disabled={
                        isPending ||
                        (formData.permissions.length === 1 &&
                          formData.permissions.includes(PERMISSIONS.ADVISOR))
                      }
                    />
                    <Label htmlFor="perm-advisor" className="font-normal cursor-pointer">
                      {t.advisorPermission}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="perm-compliance"
                      checked={formData.permissions.includes(PERMISSIONS.COMPLIANCE)}
                      onCheckedChange={() => handlePermissionToggle(PERMISSIONS.COMPLIANCE)}
                      disabled={isPending}
                    />
                    <Label htmlFor="perm-compliance" className="font-normal cursor-pointer">
                      {t.compliancePermission}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="perm-designer"
                      checked={formData.permissions.includes(PERMISSIONS.DESIGNER)}
                      onCheckedChange={() => handlePermissionToggle(PERMISSIONS.DESIGNER)}
                      disabled={isPending}
                    />
                    <Label htmlFor="perm-designer" className="font-normal cursor-pointer">
                      {t.designerPermission}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="perm-developer"
                      checked={formData.permissions.includes(PERMISSIONS.DEVELOPER)}
                      onCheckedChange={() => handlePermissionToggle(PERMISSIONS.DEVELOPER)}
                      disabled={isPending}
                    />
                    <Label htmlFor="perm-developer" className="font-normal cursor-pointer">
                      {t.developerPermission}
                    </Label>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t.permissionsHint}
                  {isEditingSsoUser && <> {t.permissionsSsoHint}</>}
                </p>
              </div>
            </div>

            <DialogFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {editingUser ? t.save : t.addUser}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ReassignClientsDialog user={reassigning} onClose={() => setReassigning(null)} />
      <ConfirmationDialog />
    </div>
  );
}
